/**
 * FormatRegistry - A singleton pattern for managing formatters
 * Allows registration and retrieval of formatting functions by key
 */

import type { FieldValue, FormattedValue, FormatterFunction } from "./types";

class FormatRegistryClass {
  private formatters: Map<string, FormatterFunction> = new Map();

  /**
   * Register a new formatter
   * @param key - Unique identifier for the formatter
   * @param formatter - The formatting function
   */
  register(key: string, formatter: FormatterFunction): void {
    this.formatters.set(key, formatter);
  }

  /**
   * Format a value using a registered formatter
   * @param key - The formatter key
   * @param value - The value to format
   * @param args - Additional arguments to pass to the formatter
   * @returns The formatted value or null
   */
  format(key: string, value: FieldValue, ...args: unknown[]): FormattedValue {
    const formatter = this.formatters.get(key);
    if (!formatter) {
      console.warn(`No formatter registered for key: ${key}`);
      return null;
    }
    return formatter(value, ...args);
  }

  /**
   * Check if a formatter is registered
   * @param key - The formatter key
   * @returns True if the formatter exists
   */
  has(key: string): boolean {
    return this.formatters.has(key);
  }

  /**
   * Get all registered formatter keys
   * @returns Array of formatter keys
   */
  keys(): string[] {
    return Array.from(this.formatters.keys());
  }

  /**
   * Remove a formatter
   * @param key - The formatter key to remove
   */
  unregister(key: string): void {
    this.formatters.delete(key);
  }

  /**
   * Clear all registered formatters
   */
  clear(): void {
    this.formatters.clear();
  }
}

// Export singleton instance
export const FormatRegistry = new FormatRegistryClass();
